<script setup lang="ts">
import BadgeItem from '@/uiKit/BadgeItem.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import IconButton from '@/uiKit/IconButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import { useId } from 'vue'

defineProps<{
  name: string
  progress: number | null
  deletable?: boolean
  status?: 'error' | 'processing' | string
  sourceType?: 'file' | 'url'
  interactive?: boolean
}>()

defineEmits<{ delete: [] }>()

const id = useId()
</script>

<template>
  <div
    v-bind="$attrs"
    :id="id"
    role="listitem"
    :aria-label="name"
    class="flex max-w-full min-w-0 cursor-pointer"
  >
    <BadgeItem
      class="max-w-full min-w-0 shrink"
      :class="[
        !deletable &&
          (status === 'error'
            ? 'hover:bg-background-critical-hovered active:bg-background-critical-pressed'
            : 'hover:bg-background-gray-subtlest-hovered active:bg-background-gray-subtlest-pressed'),
      ]"
      interactive
      :label="name"
      :variant="status === 'error' ? 'critical' : 'neutral'"
      size="sm"
    >
      <template #leading-icon>
        <CircularProgress
          v-if="status === 'processing'"
          size="xs"
          color="neutral"
        />
        <IconSprite
          v-else
          size="xs"
          :icon="sourceType === 'url' ? 'globe-fill' : 'file-fill'"
        />
      </template>

      <template #trailing-icon>
        <ToolTip
          title="Remove this file."
          :disabled="status === 'error'"
          role="tooltip"
        >
          <IconButton
            v-if="deletable"
            :aria-label="`Remove ${name}`"
            icon="close"
            size="xs"
            variant="transparent"
            :class="status === 'error' ? 'text-text-critical' : 'text-text-subtle'"
            @click="$emit('delete')"
          />
        </ToolTip>
      </template>
    </BadgeItem>
  </div>
  <ToolTip
    v-if="status === 'error'"
    title="This file did not upload correctly."
    body="If you send the message, it will be ignored. You can remove it and try adding it again."
    :target-selector="`#${id}`"
    role="tooltip"
  />
</template>
